import React, { useEffect, useState, useCallback } from 'react';
import { Avatar, Dropdown, Menu, Modal, Table, message, Tooltip } from 'antd';
import {
    UserOutlined,
    LogoutOutlined,
    HomeOutlined,
    VideoCameraOutlined,
    LoginOutlined,
    MenuOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { User } from '@/types/User';
import Colors from '../../common/color';
import { deviceService } from '../../services/deviceService';
import { privacyService } from '../../services/privacyService';
import { ColumnsType } from 'antd/es/table';

type NavigationItem = {
    key: string;
    icon: React.ReactNode;
    label: React.ReactNode;
};

const Header: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState<User | null>(null);
    const [scrolled, setScrolled] = useState(false);
    const [isLoginHistoryModalVisible, setIsLoginHistoryModalVisible] = useState(false);
    const [loginHistory, setLoginHistory] = useState<any[]>([]);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);
    const [privacy, setPrivacy] = useState<any>({});

    useEffect(() => {
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
            setUser(JSON.parse(savedUser));
        }
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 10;
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [scrolled]);

    const handleLogout = () => {
        localStorage.removeItem('user');
        navigate('/login');
    };

    const handleShowLoginHistory = async () => {
        try {
            if (!user?.id) {
                message.error('User not found');
                return;
            }
            const response = await deviceService.getByUserId(user.id);
            const devices = Array.isArray(response) ? response : response || [];
            setLoginHistory(devices);
            console.log({ devices });
            setIsLoginHistoryModalVisible(true);
        } catch (error) {
            console.error('Error fetching login history:', error);
            message.error('Cannot fetch login history');
        }
    };

    const fetchPrivacy = useCallback(async () => {
        try {
            const response = await privacyService.getPrivacy();
            setPrivacy(response || {});
        } catch (error) {
            message.error('Không thể tải chính sách');
        }
    }, []);

    const userMenu = (
        <Menu className="mt-2 rounded-xl shadow-lg border border-gray-100 p-1 min-w-[200px]">
            <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                <p className="text-xs text-gray-500 mt-1">{user?.email}</p>
            </div>
            <Menu.Item
                key={'logout'}
                icon={<LogoutOutlined className="text-red-500 text-lg" />}
                onClick={handleLogout}
                danger
                className="rounded-lg hover:bg-red-50 mt-1 font-medium"
            >
                Sign Out
            </Menu.Item>
        </Menu>
    );

    const loginHistoryColumns = [
        {
            title: 'Time Login',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: '25%',
            render: (text: string) => (
                <div className='font-medium whitespace-normal'>
                    {new Date(text).toLocaleString('vi-VN')}
                </div>
            ),
        },
        {
            title: 'Device Info',
            key: 'deviceInfo',
            width: '75%',
            render: (_, record: any) => (
                <div className='space-y-1'>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
                        <div>
                            <span className='text-gray-500'>IP:</span>{' '}
                            <span className='font-medium'>{record.ipAddress}</span>
                        </div>
                        <div>
                            <span className='text-gray-500'>Device:</span>{' '}
                            <span className='font-medium'>
                                {record.deviceType === 'desktop' ? 'Desktop' :
                                    record.deviceType === 'mobile' ? 'Mobile' : 'Tablet'}
                            </span>
                        </div>
                    </div>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
                        <div>
                            <span className='text-gray-500'>OS:</span>{' '}
                            <span className='font-medium'>{record.os}</span>
                        </div>
                        <div>
                            <span className='text-gray-500'>Browser:</span>{' '}
                            <span className='font-medium'>{record.browser}</span>
                        </div>
                    </div>
                </div>
            ),
        }
    ];

    const navigationItems: NavigationItem[] = [
        {
            key: '/',
            icon: <HomeOutlined />,
            label: 'Home',
        }
    ];
    if (user?.isViewVideo === '1') {
        navigationItems.push(
            {
                key: '/video',
                icon: <VideoCameraOutlined />,
                label: 'Video',
            }
        );
    }
    navigationItems.push(
        {
            key: '/login-history',
            icon: <LoginOutlined />,
            label: (
                <Tooltip
                    color={Colors.primary}
                    placement="rightBottom"
                    title="Hien Le Aptis Website chỉ cho phép 1 tài khoản được đăng nhập vào Website với tối đa 5 địa chỉ Wifi/4G (tính trên mọi thiết bị đăng nhập)"
                >
                    <span>Login History</span>
                </Tooltip>
            ),
        }
    );
    navigationItems.push({
        key: '/terms',
        icon: <FileTextOutlined />,
        label: 'Terms & Conditions',
    });

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-md' : 'shadow-sm'
                }`}
            style={{
                backdropFilter: scrolled ? 'blur(10px)' : 'none',
                backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.9)' : 'white',
            }}
        >
            <div className="max-w-7xl mx-auto h-[68px] px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-full">
                    {/* Logo and Navigation Links */}
                    <div className="flex items-center flex-1">
                        {/* Logo */}
                        <div
                            className="flex items-center group cursor-pointer mr-12"
                            onClick={() => navigate('/')}
                        >
                            <span className="text-2xl font-bold bg-gradient-to-r bg-clip-text text-transparent" style={{ color: Colors.primary }} >
                                HIEN LE APTIS
                            </span>
                        </div>

                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex items-center space-x-6">
                            {navigationItems.map((item) => (
                                <div
                                    key={item.key}
                                    className={`cursor-pointer flex items-center space-x-2 py-2.5 px-4 transition-all duration-300 ease-in-out
                                        ${location.pathname === item.key
                                            ? 'scale-105 shadow-sm'
                                            : 'text-gray-600'
                                        } relative overflow-hidden`}
                                    onClick={() => {
                                        if (item.key === '/login-history') {
                                            handleShowLoginHistory();
                                        } else if (item.key === '/terms') {
                                            setIsTermsModalVisible(true);
                                        } else {
                                            navigate(item.key);
                                        }
                                    }}
                                >
                                    <span className={`text-sm transition-transform duration-300 ${location.pathname === item.key ? 'scale-110' : ''
                                        }`}>{item.icon}</span>
                                    <span className="font-medium">{item.label}</span>
                                    {location.pathname === item.key && (
                                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-black" />
                                    )}
                                </div>
                            ))}
                        </nav>

                        {/* Mobile Menu Button */}
                        <button
                            className="md:hidden ml-auto"
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        >
                            <MenuOutlined className="text-2xl" style={{ color: Colors.primary }} />
                        </button>
                    </div>

                    {/* User Profile */}
                    <div className="flex items-center ml-4">
                        <Dropdown overlay={userMenu} placement="bottomRight">
                            <div className="cursor-pointer flex items-center from-gray-50 to-gray-100 p-2 rounded-xl hover:shadow-md transition-all duration-300 border border-gray-100">
                                <Avatar
                                    size={24}
                                    icon={<UserOutlined />}
                                    style={{ backgroundColor: Colors.primary }}
                                />
                                <span className="ml-2 font-medium text-gray-700 hidden sm:inline-block">
                                    {user?.name}
                                </span>
                            </div>
                        </Dropdown>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation Menu */}
            {isMobileMenuOpen && (
                <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
                    {navigationItems.map((item) => (
                        <div
                            key={item.key}
                            className={`cursor-pointer flex items-center space-x-2 p-4 transition-all duration-300 ease-in-out
                                ${location.pathname === item.key ? 'bg-gray-50' : ''}`}
                            onClick={() => {
                                if (item.key === '/login-history') {
                                    handleShowLoginHistory();
                                } else if (item.key === '/terms') {
                                    setIsTermsModalVisible(true);
                                } else {
                                    navigate(item.key);
                                }
                                setIsMobileMenuOpen(false);
                            }}
                        >
                            <span className="text-lg">{item.icon}</span>
                            <span className="font-medium">{item.label}</span>
                        </div>
                    ))}
                </div>
            )}

            <Modal
                title="Login History"
                open={isLoginHistoryModalVisible}
                onCancel={() => setIsLoginHistoryModalVisible(false)}
                footer={null}
                width="95%"
                style={{ maxWidth: 900 }}
            >
                <div className="overflow-x-auto">
                    <Table
                        dataSource={loginHistory}
                        columns={loginHistoryColumns as ColumnsType<any>}
                        rowKey="id"
                        pagination={false}
                        size="middle"
                        bordered
                        scroll={{ x: 'max-content' }}
                        locale={{
                            emptyText: 'Không có dữ liệu'
                        }}
                    />
                </div>
            </Modal>

            <Modal
                title="Terms and Conditions"
                open={isTermsModalVisible}
                onCancel={() => setIsTermsModalVisible(false)}
                footer={null}
                width={600}
                afterOpenChange={fetchPrivacy}
            >
                <div className="prose max-w-none">
                    <div dangerouslySetInnerHTML={{ __html: privacy?.content?.replace(/\n/g, '<br />') || '' }} />
                </div>
            </Modal>
        </header>
    );
};

export default Header;